import { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import styles from '../SettingsModal.module.scss'

/*
  No title here: the section's heading sits on the dialog's title line
  (SettingsModal), and it says what the section holds rather than repeating
  the highlighted entry in the navigation.
*/
interface SettingsPanelProps {
  lead?: string
  children: ReactNode
  /** Omit for a section that saves as it is edited. */
  footer?: SettingsFooterProps
}

interface SettingsFooterProps {
  /** Validates first; with nothing changed it saves nothing. */
  onSave?: () => void
  /** Instead of `onSave`: Save submits this form, which sits in the body. */
  formId?: string
}

/*
  Only Save, never disabled: greyed out on this surface it could hardly be
  seen. It checks the fields when pressed instead, and how it went is told in
  the app's banners (`showSnackbar`), as every other save is. No Cancel:
  leaving the section, by the X or another entry, already drops what was not
  saved, and asks first (SettingsModal).
*/
function SettingsFooter({ onSave, formId }: SettingsFooterProps) {
  const { t } = useTranslation()

  return (
    <div className={styles.footer}>
      <Button
        label={t('COMMON.SAVE')}
        variant="contained"
        color="primary"
        type={formId ? 'submit' : 'button'}
        form={formId}
        onClick={onSave}
      />
    </div>
  )
}

/**
 * One section of the settings modal: a lead line, a body that scrolls on its
 * own and, where the section has a Save, a footer that stays put below it. The
 * footer is a sibling of the scroller, not a child: a save button that scrolls
 * away is a save button nobody presses.
 */
function SettingsPanel({ lead, children, footer }: SettingsPanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null)

  return (
    <section className={styles.panel}>
      {lead && <p className={`body-medium ${styles.panelLead}`}>{lead}</p>}
      <div className={styles.panelBodyHost}>
        {/* The app's own bar, drawn over the body; the native one is hidden. */}
        <OverlayScrollbar targetRef={bodyRef} />
        <div className={styles.panelBody} ref={bodyRef}>
          {children}
        </div>
      </div>
      {footer && <SettingsFooter {...footer} />}
    </section>
  )
}

export default SettingsPanel
